import { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import {
  DataRoomActivityWindow,
  DEFAULT_DATA_ROOM_ACTIVITY_WINDOW,
  FileActivityRow,
  formatViewingTime,
  SpaceKind,
  ViewsTrend,
} from 'shared';
import { twJoin } from 'tailwind-merge';

import trendDownIcon from '../../../../assets/images/data-room-activity/trend-down.svg';
import trendFlatIcon from '../../../../assets/images/data-room-activity/trend-flat.svg';
import trendUpIcon from '../../../../assets/images/data-room-activity/trend-up.svg';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { Search } from '../../../../components/search/index';
import { TableRow } from '../../../../components/table/index';
import { StringKey } from '../../../../lang/index';
import { Order } from '../../../../types/general.types';
import { useDownloadSpaceFile } from '../../hooks/use-download-space-file.hook';
import { useSpacesHome } from '../../hooks/use-spaces-home.hook';
import { useViewSpaceFile } from '../../hooks/use-view-space-file.hook';
import { TreeSpaceRef } from '../../types/data-room.types';
import { BrowserFileIcon } from '../../workspaces/browser/browser-file-icon';
import { LastActive } from '../../workspaces/components/last-active';
import { ActivityEmptyState } from '../components/activity-empty-state';
import { ActivityLoadError } from '../components/activity-load-error';
import { ActivityTablePlaceholder } from '../components/activity-loading-placeholders';
import { ActivityPagedTable } from '../components/activity-paged-table';
import { ActivityWindowToggle } from '../components/activity-window-toggle';
import { DataRoomTag } from '../components/data-room-tag';
import { FileRowMenu } from '../components/file-row-menu';
import { FilterSelect } from '../components/filter-select';
import { ariaSortOf, SortableTitle } from '../components/sortable-title';
import { FileSortColumn, useActivityFiles } from '../hooks/use-activity-files.hook';
import { useOpenRoomFolder } from '../hooks/use-open-room-folder.hook';
import { ActivityOutletContext } from '../types/activity-outlet-context.types';

enum Column {
  AVG_VIEW_TIME = 'avgViewTime',
  DATA_ROOM = 'dataRoom',
  DOWNLOADS = 'downloads',
  FILE = 'file',
  FOLDER = 'folder',
  LAST_VIEWED = 'lastViewed',
  MENU = 'menu',
  UNIQUE_VIEWS = 'uniqueViews',
  VIEWS = 'views',
}

type TitledColumn = Exclude<Column, Column.MENU>;

// 20.1.3 §2, in the order the story lists them, then the row's actions. Avg. View Time is the
// column the mock draws and the text does not: §8 made viewing time a room-level clock until the
// heartbeat began naming the file on screen, and it is measured per visit that opened the file.
const COLUMN_ORDER = [
  Column.FILE,
  Column.DATA_ROOM,
  Column.FOLDER,
  Column.VIEWS,
  Column.UNIQUE_VIEWS,
  Column.DOWNLOADS,
  Column.AVG_VIEW_TIME,
  Column.LAST_VIEWED,
  Column.MENU,
];

const COLUMN_TITLE: Record<TitledColumn, StringKey> = {
  [Column.AVG_VIEW_TIME]: 'AVG_VIEW_TIME',
  [Column.DATA_ROOM]: 'DATA_ROOM',
  [Column.DOWNLOADS]: 'DOWNLOADS',
  [Column.FILE]: 'FILE',
  [Column.FOLDER]: 'FOLDER',
  [Column.LAST_VIEWED]: 'LAST_VIEWED',
  [Column.UNIQUE_VIEWS]: 'UNIQUE_VIEWS',
  [Column.VIEWS]: 'VIEWS',
};

/** 20.1.3 §5: only these four re-sort, which is also the server's allowlist. */
const SORTABLE: Partial<Record<Column, FileSortColumn>> = {
  [Column.DATA_ROOM]: FileSortColumn.DATA_ROOM,
  [Column.DOWNLOADS]: FileSortColumn.DOWNLOADS,
  [Column.UNIQUE_VIEWS]: FileSortColumn.UNIQUE_VIEWS,
  [Column.VIEWS]: FileSortColumn.VIEWS,
};

// A column's first click asks for the direction it is read in, as on the Visitors tab: rooms A–Z,
// the counts most first.
const FIRST_ORDER: Record<FileSortColumn, Order> = {
  [FileSortColumn.DATA_ROOM]: Order.ASC,
  [FileSortColumn.DOWNLOADS]: Order.DESC,
  [FileSortColumn.UNIQUE_VIEWS]: Order.DESC,
  [FileSortColumn.VIEWS]: Order.DESC,
};

// The design draws "down" as the red arrow turned over.
const TREND_ICON: Record<ViewsTrend, { className?: string; src: string }> = {
  [ViewsTrend.DOWN]: { className: 'rotate-180', src: trendDownIcon },
  [ViewsTrend.FLAT]: { src: trendFlatIcon },
  [ViewsTrend.UP]: { src: trendUpIcon },
};

// §3: the arrow compares this window with the one of equal length before it. It is rendered, never
// stored, and it appears on this tab alone.
const ViewsCell: FC<{ row: FileActivityRow }> = ({ row }) => (
  <span className="flex items-center gap-2">
    {row.views}
    <img
      alt=""
      className={twJoin('size-4', TREND_ICON[row.trend].className)}
      src={TREND_ICON[row.trend].src}
    />
  </span>
);

const FileName: FC<{ name: string }> = ({ name }) => (
  <span className="flex min-w-0 items-center gap-2">
    <BrowserFileIcon fileName={name} size={16} />
    <span className="truncate" title={name}>
      {name}
    </span>
  </span>
);

/**
 * The Files tab — 20.1.3.
 *
 * Every file of every live data room, whether or not anybody opened it (§7). The counts, the
 * trend and the last view all arrive decided by the read layer; this sorts nothing and computes
 * nothing of its own.
 */
export const ActivityFilesTab: FC = () => {
  const openFolder = useOpenRoomFolder();
  const { toolbar } = useOutletContext<ActivityOutletContext>();
  const viewFile = useViewSpaceFile();
  const downloadFile = useDownloadSpaceFile();
  const [window, setWindow] = useState<DataRoomActivityWindow>(DEFAULT_DATA_ROOM_ACTIVITY_WINDOW);
  const [dataRoomId, setDataRoomId] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<FileSortColumn>(FileSortColumn.VIEWS);
  const [sortOrder, setSortOrder] = useState<Order>(Order.DESC);

  // Every filter and order is a query of its own, so each starts from its first page again.
  const { count, isError, isPending, pagination, retry, rows } = useActivityFiles({
    dataRoomId: dataRoomId || undefined,
    search: search.trim() || undefined,
    sortBy,
    sortOrder,
    window,
  });
  // The room filter lists every data room of the company — the same cached list Workspaces opens
  // with — rather than one page of the activity table, which would drop rooms past its first 20.
  const { home } = useSpacesHome();

  const reSort = (column: Column): void => {
    const next = SORTABLE[column];

    if (!next) return;

    setSortOrder(
      next === sortBy ? (sortOrder === Order.DESC ? Order.ASC : Order.DESC) : FIRST_ORDER[next],
    );
    setSortBy(next);
  };

  const fileSpace = (
    row: FileActivityRow,
  ): { fileId: string; fileName: string; space: TreeSpaceRef } => ({
    fileId: row.fileId,
    fileName: row.name,
    space: { id: row.dataRoomId, kind: SpaceKind.DATA_ROOM },
  });

  const toolbarControls = (
    <div className="flex items-center gap-3">
      {/* §4 */}
      <Search buttonClassName="bg-white shadow-xs" callback={setSearch} />
      <FilterSelect
        allLabel="ALL_DATA_ROOMS"
        onChange={setDataRoomId}
        options={(home?.dataRooms ?? []).map((room) => ({ label: room.name, value: room.id }))}
        value={dataRoomId}
      />
      <ActivityWindowToggle onChange={setWindow} value={window} />
    </div>
  );

  const renderContent = () => {
    if (isPending) {
      return <ActivityTablePlaceholder columns={8} />;
    }

    if (isError) {
      return <ActivityLoadError onRetry={() => void retry()} />;
    }

    // §7: nothing uploaded anywhere, and nothing matching, are different things to say.
    if (count === 0) {
      return (
        <div className="flex h-full min-h-0 w-full flex-1 flex-col items-center">
          {search.trim() || dataRoomId ? (
            <span className="pt-[72px] text-sm font-[450] text-gray-400">
              <AppFormattedMessage id="NO_FILES_MATCH_SEARCH" />
            </span>
          ) : (
            <ActivityEmptyState
              description="NO_FILES_IN_DATA_ROOMS_HINT"
              title="NO_FILES_IN_DATA_ROOMS"
            />
          )}
        </div>
      );
    }

    return (
      // Fades in on arrival; a new window, filter or order dims it while its first page loads.
      <div
        className={twJoin(
          'flex h-full min-h-0 w-full flex-1 animate-reveal-fade flex-col gap-4 overflow-y-auto p-4 transition-opacity duration-200',
          pagination.isReloading && 'opacity-60',
        )}
      >
        <ActivityPagedTable
          ariaSort={ariaSortOf(SORTABLE, sortBy, sortOrder)}
          className="min-w-[1210px]"
          columnOrder={COLUMN_ORDER}
          columnsTitle={{
            ...Object.fromEntries(
              Object.entries(COLUMN_TITLE).map(([column, title]) => [
                column,
                <SortableTitle
                  key={column}
                  onSort={SORTABLE[column as Column] && (() => reSort(column as Column))}
                  title={title}
                />,
              ]),
            ),
            [Column.MENU]: null,
          }}
          pagination={pagination}
          renderRow={(row, className) => (
            <TableRow
              columnOrder={COLUMN_ORDER}
              contentClassName={{ general: 'gap-2 px-4', [Column.MENU]: 'justify-center px-3' }}
              fields={{
                id: row.fileId,
                [Column.FILE]: <FileName name={row.name} />,
                [Column.DATA_ROOM]: <DataRoomTag name={row.dataRoomName} />,
                [Column.FOLDER]: (
                  <button
                    className="block max-w-full truncate text-brand-700 hover:underline"
                    // The folder itself, opened in Workspaces.
                    onClick={() => void openFolder(row)}
                    title={row.folderName}
                    type="button"
                  >
                    {row.folderName}
                  </button>
                ),
                [Column.VIEWS]: <ViewsCell row={row} />,
                [Column.UNIQUE_VIEWS]: row.uniqueViews,
                [Column.DOWNLOADS]: row.downloads,
                // Per visit that opened it, so one long read outranks a file everybody glances at.
                [Column.AVG_VIEW_TIME]: formatViewingTime(row.avgViewingTimeMs),
                // 20.1.0 §9, and views only — a download never moves this.
                [Column.LAST_VIEWED]: <LastActive at={row.lastViewedAt} />,
                [Column.MENU]: (
                  <FileRowMenu
                    onDownload={() => void downloadFile(fileSpace(row))}
                    onView={() => void viewFile(fileSpace(row))}
                  />
                ),
              }}
              key={row.fileId}
              labelClassName="min-w-0 text-sm"
              tdClassName="p-0"
              trClassName={className}
            />
          )}
          rows={rows}
          tHeadClassNames={{
            thClassName: {
              // The file's name takes what is left; every other column is sized to what it holds.
              general: 'px-4',
              [Column.AVG_VIEW_TIME]: 'w-[130px]',
              [Column.DATA_ROOM]: 'w-[180px]',
              [Column.DOWNLOADS]: 'w-28',
              [Column.FOLDER]: 'w-[140px]',
              [Column.LAST_VIEWED]: 'w-[150px]',
              [Column.MENU]: 'w-14',
              [Column.UNIQUE_VIEWS]: 'w-[120px]',
              [Column.VIEWS]: 'w-24',
            },
          }}
        />
      </div>
    );
  };

  return (
    <>
      {/* Drawn beside the sub-tab buttons, but every filter here stays this tab's own. */}
      {toolbar && createPortal(toolbarControls, toolbar)}
      {renderContent()}
    </>
  );
};
